import React, { useState } from "react";
import {
  Database, Plus, CheckCircle2, AlertTriangle, XCircle,
  Server, Cloud, KeyRound, Users, Bell, ChevronRight, ChevronLeft,
  Check, Loader2, TestTube, Table2,
} from "lucide-react";
import { DATASETS } from "@/data/mockData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import DataTable from "@/components/DataTable";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS = {
  connected: { icon: CheckCircle2, color: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10", label: "Connected" },
  warning: { icon: AlertTriangle, color: "text-amber-500 border-amber-500/40 bg-amber-500/10", label: "Delayed" },
  disconnected: { icon: XCircle, color: "text-red-500 border-red-500/40 bg-red-500/10", label: "Disconnected" },
};

const SAMPLE_ROWS = [
  { code: "CUST-10428", name: "Sarah Mitchell", segment: "Premium", country: "US", risk: 0.14, lifetime_value: 82440 },
  { code: "CUST-10429", name: "Michael Thompson", segment: "Business", country: "CA", risk: 0.31, lifetime_value: 118220 },
  { code: "CUST-10430", name: "Jennifer Anderson", segment: "Retail", country: "US", risk: 0.42, lifetime_value: 21980 },
  { code: "CUST-10431", name: "David Wilson", segment: "Youth", country: "CA", risk: 0.08, lifetime_value: 6420 },
  { code: "CUST-10432", name: "Emily Roberts", segment: "Premium", country: "GB", risk: 0.11, lifetime_value: 94120 },
  { code: "CUST-10433", name: "Christopher Brown", segment: "Business", country: "US", risk: 0.22, lifetime_value: 68910 },
  { code: "CUST-10434", name: "Jessica Taylor", segment: "Retail", country: "CA", risk: 0.19, lifetime_value: 34450 },
  { code: "CUST-10435", name: "Matthew Johnson", segment: "Premium", country: "US", risk: 0.09, lifetime_value: 156700 },
  { code: "CUST-10436", name: "Amanda Davis", segment: "Retail", country: "US", risk: 0.28, lifetime_value: 18990 },
  { code: "CUST-10437", name: "Daniel Martinez", segment: "Business", country: "CA", risk: 0.16, lifetime_value: 102340 },
  { code: "CUST-10438", name: "Ashley Miller", segment: "Youth", country: "US", risk: 0.06, lifetime_value: 4820 },
  { code: "CUST-10439", name: "Ryan Cooper", segment: "Premium", country: "CA", risk: 0.13, lifetime_value: 87560 },
  { code: "CUST-10440", name: "Nicole Adams", segment: "Retail", country: "US", risk: 0.35, lifetime_value: 22110 },
  { code: "CUST-10441", name: "Kevin O'Brien", segment: "Business", country: "US", risk: 0.24, lifetime_value: 76500 },
  { code: "CUST-10442", name: "Rachel Nguyen", segment: "Premium", country: "CA", risk: 0.10, lifetime_value: 111890 },
];

export default function Configuration() {
  const [datasets, setDatasets] = useState(DATASETS);
  const [openConnect, setOpenConnect] = useState(false);

  const testConnection = (id) => {
    toast.success("Connection test passed · 42 ms round-trip");
    setDatasets((ds) => ds.map((d) => (d.id === id ? { ...d, status: "connected", last_sync: "just now" } : d)));
  };

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div>
        <div className="label-caps mb-2">Workspace · admin only</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Configuration</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-xl">
          Manage data sources, security, integrations, and platform settings.
        </p>
      </div>

      <Tabs defaultValue="datasets" className="w-full">
        <TabsList className="bg-secondary/60 h-11">
          <TabsTrigger value="datasets" data-testid="config-tab-datasets" className="gap-2 data-[state=active]:bg-card"><Database className="w-3.5 h-3.5" /> Data sources</TabsTrigger>
          <TabsTrigger value="team" data-testid="config-tab-team" className="gap-2 data-[state=active]:bg-card"><Users className="w-3.5 h-3.5" /> Team</TabsTrigger>
          <TabsTrigger value="security" data-testid="config-tab-security" className="gap-2 data-[state=active]:bg-card"><KeyRound className="w-3.5 h-3.5" /> Security</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="config-tab-notifications" className="gap-2 data-[state=active]:bg-card"><Bell className="w-3.5 h-3.5" /> Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="datasets" className="mt-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{datasets.length} sources · {datasets.filter((d) => d.status === "connected").length} healthy</div>
            <Button onClick={() => setOpenConnect(true)} data-testid="config-add-dataset" className="gap-2 bg-primary hover:bg-[#0B2B4E]">
              <Plus className="w-4 h-4" /> Connect dataset
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {datasets.map((d) => {
              const s = STATUS[d.status];
              const Icon = s.icon;
              return (
                <Card key={d.id} data-testid={`dataset-${d.id}`} className="p-5 border-border hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                        {d.type.includes("Kafka") || d.type.includes("BigQuery") ? <Cloud className="w-4 h-4 text-primary" strokeWidth={1.5} /> : <Server className="w-4 h-4 text-primary" strokeWidth={1.5} />}
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold">{d.name}</div>
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">{d.type} · {d.host}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className={cn("border gap-1", s.color)}>
                      <Icon className="w-3 h-3" /> {s.label}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 my-4">
                    <div>
                      <div className="label-caps">Records</div>
                      <div className="font-display text-lg font-semibold">{d.records}</div>
                    </div>
                    <div>
                      <div className="label-caps">Last sync</div>
                      <div className="font-display text-lg font-semibold">{d.last_sync}</div>
                    </div>
                  </div>

                  <div className="flex gap-2 border-t border-border pt-3">
                    <Button variant="outline" size="sm" onClick={() => testConnection(d.id)} data-testid={`test-${d.id}`} className="gap-1.5">
                      <TestTube className="w-3 h-3" /> Test connection
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs">Configure</Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Preview */}
          <Card className="border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps flex items-center gap-2"><Table2 className="w-3.5 h-3.5" /> Preview · Core Banking · Customer Master</div>
                <div className="font-display text-xl font-semibold mt-1">Live sample data</div>
              </div>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 bg-emerald-500/10 gap-1"><CheckCircle2 className="w-3 h-3" /> Schema mapped</Badge>
            </div>

            <DataTable
              testIdPrefix="config-preview-table"
              initialPageSize={10}
              columns={[
                { key: "code", header: "Customer Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
                { key: "name", header: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
                { key: "segment", header: "Segment", render: (r) => <Badge variant="outline">{r.segment}</Badge> },
                { key: "country", header: "Country", render: (r) => <span className="text-muted-foreground">{r.country}</span> },
                {
                  key: "risk", header: "Risk score",
                  render: (r) => (
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${r.risk * 100}%` }} />
                      </div>
                      <span className="font-mono text-xs">{r.risk.toFixed(2)}</span>
                    </div>
                  ),
                },
                { key: "lifetime_value", header: "LTV (USD)", render: (r) => <span className="font-mono">{r.lifetime_value.toLocaleString()}</span> },
              ]}
              data={SAMPLE_ROWS}
            />
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <Card className="border-border p-6">
            <div className="label-caps mb-1">Workspace members · 1</div>
            <div className="font-display text-xl font-semibold mb-4">Team</div>
            <div className="flex items-center gap-4 p-4 border border-border rounded-md">
              <div className="w-11 h-11 rounded-md bg-primary/10 border border-primary/30 text-primary font-display font-semibold text-base flex items-center justify-center">AK</div>
              <div className="flex-1">
                <div className="font-medium">admin@optikurve.com</div>
                <div className="text-xs text-muted-foreground">Platform Administrator · You</div>
              </div>
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10">Owner</Badge>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              This is an on-premise workspace. Additional seats can be provisioned via your identity provider (SAML / LDAP).
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-4">
          <Card className="border-border p-6 space-y-4">
            <div className="font-display text-xl font-semibold">Security policies</div>
            <ToggleRow label="Two-factor authentication" desc="Require TOTP for all admin actions" defaultChecked />
            <ToggleRow label="Payload encryption at rest" desc="AES-256 encryption on all stored event payloads" defaultChecked />
            <ToggleRow label="PII redaction in logs" desc="Automatically mask card numbers and national IDs" defaultChecked />
            <ToggleRow label="Anomalous login blocking" desc="Auto-block sessions from unrecognized geographies" />
          </Card>

          <Card className="border-border p-6">
            <div className="label-caps mb-2">API Keys</div>
            <div className="font-display text-xl font-semibold mb-4">Access tokens</div>
            <div className="p-4 border border-border rounded-md flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">optikurve-prod-ingest</div>
                <div className="font-mono text-xs text-muted-foreground">optk_live_••••••••••4c8a · created 42 days ago</div>
              </div>
              <Button variant="outline" size="sm">Rotate</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <Card className="border-border p-6 space-y-4">
            <div className="font-display text-xl font-semibold">Notification preferences</div>
            <ToggleRow label="Detection rule triggers" desc="Email me when a critical detection fires" defaultChecked />
            <ToggleRow label="Workflow failures" desc="Notify on workflow errors" defaultChecked />
            <ToggleRow label="Weekly AI digest" desc="Summary of new AI-surfaced insights every Monday" defaultChecked />
            <ToggleRow label="Ingestion drops" desc="Alert if event ingestion falls below 500 events/min" />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connect wizard */}
      <ConnectWizard open={openConnect} onOpenChange={setOpenConnect} onDone={(ds) => {
        setDatasets((prev) => [ds, ...prev]);
        setOpenConnect(false);
        toast.success(`Dataset "${ds.name}" connected`);
      }} />
    </div>
  );
}

/* ================= Connect Wizard (multi-step) ================= */

const WIZARD_STEPS = [
  { key: "type", label: "Source type" },
  { key: "connection", label: "Connection" },
  { key: "test", label: "Test" },
  { key: "table", label: "Table selection" },
  { key: "mapping", label: "Column mapping" },
  { key: "review", label: "Review & save" },
];

const SOURCE_TYPES = [
  { id: "postgres", label: "PostgreSQL", icon: Server, hint: "TCP · port 5432", protocol: "postgresql://" },
  { id: "snowflake", label: "Snowflake", icon: Cloud, hint: "Cloud data warehouse", protocol: "snowflake://" },
  { id: "bigquery", label: "BigQuery", icon: Cloud, hint: "Google Cloud", protocol: "bigquery://" },
  { id: "kafka", label: "Kafka Stream", icon: Cloud, hint: "Real-time ingest", protocol: "kafka://" },
  { id: "oracle", label: "Oracle 19c", icon: Server, hint: "Legacy on-premise", protocol: "oracle://" },
  { id: "mysql", label: "MySQL", icon: Server, hint: "TCP · port 3306", protocol: "mysql://" },
];

const MOCK_TABLES = {
  postgres: [
    { name: "public.customer", rows: "1.2M", columns: 18 },
    { name: "public.account", rows: "1.4M", columns: 12 },
    { name: "public.card", rows: "820K", columns: 14 },
    { name: "public.transaction", rows: "42.8M", columns: 22 },
  ],
  snowflake: [
    { name: "CRM.CONTACT", rows: "980K", columns: 24 },
    { name: "CRM.PREFERENCES", rows: "980K", columns: 16 },
  ],
  bigquery: [
    { name: "marketing.attribution_events", rows: "4.1M", columns: 20 },
  ],
  kafka: [
    { name: "card.txn.stream.v3", rows: "3.8M / day", columns: 15 },
    { name: "auth.event.stream.v2", rows: "1.2M / day", columns: 11 },
  ],
  oracle: [
    { name: "LOANS.APPLICATION", rows: "215K", columns: 28 },
  ],
  mysql: [
    { name: "core.customer", rows: "1.2M", columns: 18 },
  ],
};

const MOCK_COLUMNS = [
  { name: "customer_code", type: "VARCHAR(16)", nullable: false, sample: "CUST-10428" },
  { name: "first_name", type: "VARCHAR(80)", nullable: false, sample: "Sarah" },
  { name: "last_name", type: "VARCHAR(80)", nullable: false, sample: "Mitchell" },
  { name: "email_addr", type: "VARCHAR(120)", nullable: true, sample: "s.mitchell@…" },
  { name: "country_iso", type: "CHAR(2)", nullable: false, sample: "US" },
  { name: "segment_cd", type: "VARCHAR(16)", nullable: true, sample: "PREMIUM" },
  { name: "risk_score", type: "NUMERIC(4,3)", nullable: true, sample: "0.140" },
  { name: "ltv_amount", type: "NUMERIC(12,2)", nullable: true, sample: "82440.00" },
  { name: "created_ts", type: "TIMESTAMP", nullable: false, sample: "2024-06-11T…" },
  { name: "updated_ts", type: "TIMESTAMP", nullable: false, sample: "2026-02-13T…" },
];

const TARGET_FIELDS = [
  { id: "-", label: "— ignore —" },
  { id: "customer.code", label: "customer.code (primary key)" },
  { id: "customer.first_name", label: "customer.first_name" },
  { id: "customer.last_name", label: "customer.last_name" },
  { id: "customer.email", label: "customer.email" },
  { id: "customer.country", label: "customer.country" },
  { id: "customer.segment", label: "customer.segment" },
  { id: "customer.risk_score", label: "customer.risk_score" },
  { id: "customer.lifetime_value", label: "customer.lifetime_value" },
  { id: "customer.created_at", label: "customer.created_at" },
  { id: "customer.updated_at", label: "customer.updated_at" },
];

// Best-guess auto suggestion (deterministic, not "AI")
const suggestTarget = (colName) => {
  const n = colName.toLowerCase();
  if (n.includes("code") && n.includes("customer")) return "customer.code";
  if (n.includes("first_name")) return "customer.first_name";
  if (n.includes("last_name")) return "customer.last_name";
  if (n.includes("email")) return "customer.email";
  if (n.includes("country")) return "customer.country";
  if (n.includes("segment")) return "customer.segment";
  if (n.includes("risk")) return "customer.risk_score";
  if (n.includes("ltv") || n.includes("lifetime")) return "customer.lifetime_value";
  if (n.includes("created")) return "customer.created_at";
  if (n.includes("updated")) return "customer.updated_at";
  return "-";
};

function ConnectWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(0);
  const [type, setType] = useState("postgres");
  const [conn, setConn] = useState({ name: "", host: "", port: "5432", database: "", username: "", password: "", ssl: true });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok: bool, ms }
  const [selectedTable, setSelectedTable] = useState(null);
  const [mapping, setMapping] = useState({});
  const [syncMode, setSyncMode] = useState("incremental");
  const [syncInterval, setSyncInterval] = useState("15m");

  React.useEffect(() => {
    if (open) {
      setStep(0); setType("postgres");
      setConn({ name: "", host: "", port: "5432", database: "", username: "", password: "", ssl: true });
      setTesting(false); setTestResult(null); setSelectedTable(null); setMapping({});
      setSyncMode("incremental"); setSyncInterval("15m");
    }
  }, [open]);

  const tables = MOCK_TABLES[type] || [];

  React.useEffect(() => {
    // When selecting a table, seed mapping with suggestions
    if (selectedTable) {
      const seed = {};
      MOCK_COLUMNS.forEach((c) => { seed[c.name] = suggestTarget(c.name); });
      setMapping(seed);
    }
  }, [selectedTable]);

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    await new Promise((r) => setTimeout(r, 900));
    if (!conn.host || !conn.database || !conn.username) {
      setTesting(false);
      setTestResult({ ok: false, error: "Missing host, database or username" });
      return;
    }
    setTestResult({ ok: true, ms: 42, server_version: "PostgreSQL 15.4" });
    setTesting(false);
  };

  const canNext = () => {
    if (step === 1) return conn.name && conn.host && conn.database && conn.username;
    if (step === 2) return testResult?.ok;
    if (step === 3) return !!selectedTable;
    if (step === 4) return Object.values(mapping).some((v) => v !== "-");
    return true;
  };

  const finish = () => {
    const mapped = Object.values(mapping).filter((v) => v !== "-").length;
    onDone({
      id: `ds_${Date.now()}`,
      name: conn.name,
      type: SOURCE_TYPES.find((s) => s.id === type)?.label || type,
      host: `${conn.host}:${conn.port}`,
      status: "connected",
      records: selectedTable?.rows || "—",
      last_sync: "just now",
      table: selectedTable?.name,
      mapped_fields: mapped,
      sync_mode: syncMode,
      sync_interval: syncInterval,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="connect-wizard">
        <DialogHeader>
          <div className="label-caps mb-2">New connection · Step {step + 1} of {WIZARD_STEPS.length}</div>
          <DialogTitle className="font-display text-2xl">{WIZARD_STEPS[step].label}</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 py-3 border-y border-border overflow-x-auto">
          {WIZARD_STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <button
                onClick={() => i < step && setStep(i)}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-1 rounded-sm text-xs whitespace-nowrap transition-colors",
                  i === step && "text-primary font-medium",
                  i < step && "text-foreground hover:bg-secondary",
                  i > step && "text-muted-foreground cursor-default"
                )}
              >
                <span className={cn(
                  "w-5 h-5 rounded-sm border flex items-center justify-center text-[0.65rem] font-mono",
                  i < step && "bg-primary border-primary text-primary-foreground",
                  i === step && "border-primary text-primary",
                  i > step && "border-border text-muted-foreground"
                )}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                {s.label}
              </button>
              {i < WIZARD_STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-auto py-4">
          {step === 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="wizard-step-type">
              {SOURCE_TYPES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setType(s.id)}
                  data-testid={`source-${s.id}`}
                  className={cn(
                    "p-4 border rounded-md text-left transition-colors",
                    type === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <s.icon className="w-5 h-5 text-primary mb-3" strokeWidth={1.5} />
                  <div className="font-medium text-sm">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.hint}</div>
                  <div className="font-mono text-[0.65rem] text-muted-foreground mt-2">{s.protocol}</div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-connection">
              <Field label="Display name">
                <Input value={conn.name} onChange={(e) => setConn({ ...conn, name: e.target.value })} placeholder="e.g. Core Banking — Customer Master" data-testid="wiz-name" />
              </Field>
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <Field label="Host">
                  <Input value={conn.host} onChange={(e) => setConn({ ...conn, host: e.target.value })} placeholder="host.internal.optikurve.io" className="font-mono text-xs" data-testid="wiz-host" />
                </Field>
                <Field label="Port">
                  <Input value={conn.port} onChange={(e) => setConn({ ...conn, port: e.target.value })} className="font-mono text-xs" data-testid="wiz-port" />
                </Field>
              </div>
              <Field label="Database / Schema">
                <Input value={conn.database} onChange={(e) => setConn({ ...conn, database: e.target.value })} placeholder="core_banking" className="font-mono text-xs" data-testid="wiz-database" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Username">
                  <Input value={conn.username} onChange={(e) => setConn({ ...conn, username: e.target.value })} data-testid="wiz-user" />
                </Field>
                <Field label="Password">
                  <Input type="password" value={conn.password} onChange={(e) => setConn({ ...conn, password: e.target.value })} data-testid="wiz-pass" />
                </Field>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-md">
                <div>
                  <div className="text-sm font-medium">SSL / TLS</div>
                  <div className="text-xs text-muted-foreground">Encrypt in-transit connection (recommended)</div>
                </div>
                <Switch checked={conn.ssl} onCheckedChange={(v) => setConn({ ...conn, ssl: v })} data-testid="wiz-ssl" />
              </div>
              <div className="border border-border rounded-md p-3 bg-secondary/40">
                <div className="label-caps mb-1">Connection URI (preview)</div>
                <code className="font-mono text-xs text-primary break-all">
                  {SOURCE_TYPES.find((s) => s.id === type)?.protocol}{conn.username || "user"}:••••@{conn.host || "host"}:{conn.port}/{conn.database || "db"}{conn.ssl ? "?sslmode=require" : ""}
                </code>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-test">
              <div className="border border-border rounded-md p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-medium text-sm mb-1">Verify connectivity</div>
                    <div className="text-xs text-muted-foreground">A test query will be executed against your endpoint.</div>
                  </div>
                  <Button onClick={runTest} disabled={testing} data-testid="wiz-test-run" className="gap-2 bg-primary hover:bg-[#0B2B4E]">
                    {testing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing…</> : <><TestTube className="w-3.5 h-3.5" /> Run test</>}
                  </Button>
                </div>

                {testing && (
                  <div className="space-y-2 text-xs text-muted-foreground font-mono">
                    <div>&gt; opening TCP socket to {conn.host}:{conn.port}…</div>
                    <div>&gt; negotiating {conn.ssl ? "TLS 1.3" : "plain"}…</div>
                    <div>&gt; authenticating as {conn.username}…</div>
                    <div>&gt; executing <span className="text-primary">SELECT 1</span>…</div>
                  </div>
                )}

                {testResult?.ok && (
                  <div className="mt-2 border border-emerald-500/30 bg-emerald-500/5 rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="font-medium text-sm">Connection successful</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                      <span>Round-trip: <span className="text-emerald-500">{testResult.ms} ms</span></span>
                      <span>Server: <span className="text-emerald-500">{testResult.server_version}</span></span>
                      <span>TLS: <span className="text-emerald-500">{conn.ssl ? "1.3" : "disabled"}</span></span>
                    </div>
                  </div>
                )}

                {testResult?.ok === false && (
                  <div className="mt-2 border border-red-500/30 bg-red-500/5 rounded-md p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="font-medium text-sm">Connection failed</span>
                    </div>
                    <div className="text-xs font-mono text-red-500">{testResult.error}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-table">
              <div className="text-xs text-muted-foreground">Choose which table to synchronise. You can add more later.</div>
              <div className="border border-border rounded-md divide-y divide-border">
                {tables.map((t) => (
                  <button
                    key={t.name}
                    onClick={() => setSelectedTable(t)}
                    data-testid={`wiz-table-${t.name}`}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                      selectedTable?.name === t.name ? "bg-primary/5" : "hover:bg-secondary/60"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-sm border flex items-center justify-center",
                      selectedTable?.name === t.name ? "border-primary bg-primary" : "border-border"
                    )}>
                      {selectedTable?.name === t.name && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <Table2 className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="font-mono text-sm">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.columns} columns</div>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">{t.rows} rows</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-mapping">
              <div className="text-xs text-muted-foreground">
                Map each source column to a target field in the OptiKurve domain model. Set to <span className="font-mono">— ignore —</span> to skip.
              </div>
              <div className="border border-border rounded-md overflow-hidden">
                <div className="grid grid-cols-[1.4fr_1fr_0.8fr_1.6fr] gap-2 px-4 py-2 bg-secondary/60 label-caps text-[0.55rem]">
                  <span>Source column</span>
                  <span>Type</span>
                  <span>Sample</span>
                  <span>Target field</span>
                </div>
                {MOCK_COLUMNS.map((col) => (
                  <div key={col.name} className="grid grid-cols-[1.4fr_1fr_0.8fr_1.6fr] gap-2 items-center px-4 py-2 border-t border-border">
                    <div className="font-mono text-xs">{col.name}{!col.nullable && <span className="text-red-500 ml-0.5">*</span>}</div>
                    <div className="font-mono text-xs text-muted-foreground">{col.type}</div>
                    <div className="font-mono text-xs text-muted-foreground truncate">{col.sample}</div>
                    <Select value={mapping[col.name] || "-"} onValueChange={(v) => setMapping({ ...mapping, [col.name]: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`map-${col.name}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TARGET_FIELDS.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="text-foreground font-medium">{Object.values(mapping).filter((v) => v !== "-").length}</span> of {MOCK_COLUMNS.length} columns mapped
              </div>

              <div className="pt-3 border-t border-border grid grid-cols-2 gap-3">
                <Field label="Sync mode">
                  <Select value={syncMode} onValueChange={setSyncMode}>
                    <SelectTrigger data-testid="wiz-sync-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full snapshot (each run)</SelectItem>
                      <SelectItem value="incremental">Incremental (by updated_ts)</SelectItem>
                      <SelectItem value="cdc">Change data capture (CDC)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Sync interval">
                  <Select value={syncInterval} onValueChange={setSyncInterval}>
                    <SelectTrigger data-testid="wiz-sync-interval"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1m">Every 1 minute</SelectItem>
                      <SelectItem value="15m">Every 15 minutes</SelectItem>
                      <SelectItem value="1h">Hourly</SelectItem>
                      <SelectItem value="1d">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3" data-testid="wizard-step-review">
              <div className="border border-border rounded-md p-4 space-y-2 text-sm">
                <ReviewRow label="Source type" value={SOURCE_TYPES.find((s) => s.id === type)?.label} />
                <ReviewRow label="Name" value={conn.name} />
                <ReviewRow label="Endpoint" value={`${conn.host}:${conn.port}/${conn.database}`} mono />
                <ReviewRow label="TLS" value={conn.ssl ? "Enabled (v1.3)" : "Disabled"} />
                <ReviewRow label="Table" value={selectedTable?.name} mono />
                <ReviewRow label="Columns mapped" value={`${Object.values(mapping).filter((v) => v !== "-").length} / ${MOCK_COLUMNS.length}`} />
                <ReviewRow label="Sync" value={`${syncMode} · every ${syncInterval}`} />
              </div>
              <div className="text-xs text-muted-foreground border border-primary/30 bg-primary/5 rounded-md p-3">
                OptiKurve will store credentials encrypted, run an initial import, and schedule ongoing sync. You can pause or reconfigure at any time.
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={() => step === 0 ? onOpenChange(false) : setStep((s) => s - 1)}
            data-testid="wiz-prev"
            className="gap-2"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step === WIZARD_STEPS.length - 1 ? (
            <Button onClick={finish} data-testid="wiz-finish" className="bg-primary hover:bg-[#0B2B4E] gap-2">
              <Check className="w-3.5 h-3.5" /> Save connection
            </Button>
          ) : (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext()}
              data-testid="wiz-next"
              className="bg-primary hover:bg-[#0B2B4E] gap-2"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ReviewRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between border-b border-border last:border-0 pb-2 last:pb-0">
    <span className="label-caps">{label}</span>
    <span className={mono ? "font-mono text-xs" : "text-sm"}>{value || "—"}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="label-caps">{label}</Label>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, defaultChecked }) => {
  const [on, setOn] = useState(!!defaultChecked);
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={on} onCheckedChange={setOn} />
    </div>
  );
};
